import { DAMAGE_TYPES, type DamageType, type MovementType } from '../characters/types';
import type { Rng } from '../rng/seededRandom';
import { MAX_ARENA_SIZE, TileMap } from './tilemap';
import type { ArenaDef, TilesetDef, TileWalk } from './types';

/** Seats an arena may carry, one spawn each (D-07, D-19). */
export const MAX_SEATS = 6;

/** Default seconds before the fittest fighter wins, when an arena names none. */
export const DEFAULT_TIMEOUT = 120;
/** Default seconds before the safe ring starts shrinking, when an arena names none. */
export const DEFAULT_SUDDEN_DEATH_AT = 90;

const WALKS: readonly TileWalk[] = ['ground', 'liquid', 'blocked'];
const ID = /^[a-z0-9][a-z0-9-]{0,63}$/;
const COLOUR = /^#[0-9a-fA-F]{6}$/;

function fail(what: string, message: string): never {
  throw new Error(`Invalid ${what}: ${message}`);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function optionalNumber(v: unknown, what: string, field: string, min: number, max: number): void {
  if (v === undefined) return;
  if (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max)
    fail(what, `${field} must be a number between ${min} and ${max}`);
}

export function validateTilesetDef(input: unknown): TilesetDef {
  if (!isRecord(input)) fail('tileset', 'expected an object');
  const id = input.id;
  if (typeof id !== 'string' || !ID.test(id)) fail('tileset', 'id must be kebab-case');
  const what = `tileset '${id}'`;
  if (input.name !== undefined && typeof input.name !== 'string')
    fail(what, 'name must be a string');
  if (!Array.isArray(input.tiles) || input.tiles.length === 0) fail(what, 'tiles is required');
  const seen = new Set<string>();
  for (const raw of input.tiles) {
    if (!isRecord(raw)) fail(what, 'every tile must be an object');
    if (typeof raw.id !== 'string' || !ID.test(raw.id))
      fail(what, 'every tile id must be kebab-case');
    if (seen.has(raw.id)) fail(what, `tile '${raw.id}' is defined twice`);
    seen.add(raw.id);
    const tile = `${what} tile '${raw.id}'`;
    if (typeof raw.walk !== 'string' || !WALKS.includes(raw.walk as TileWalk))
      fail(tile, 'walk must be ground, liquid or blocked');
    if (typeof raw.height !== 'number' || !Number.isFinite(raw.height) || raw.height < 0)
      fail(tile, 'height must be a metre count of 0 or more');
    optionalNumber(raw.speedScale, tile, 'speedScale', 0.1, 4);
    optionalNumber(raw.friction, tile, 'friction', 0, 1);
    optionalNumber(raw.damagePerSecond, tile, 'damagePerSecond', 0, 1000);
    if (raw.damageType !== undefined && !DAMAGE_TYPES.includes(raw.damageType as DamageType))
      fail(tile, `damageType must be one of ${DAMAGE_TYPES.join(', ')}`);
    if (raw.light !== undefined) {
      if (!isRecord(raw.light) || typeof raw.light.color !== 'string')
        fail(tile, 'light must carry a colour');
      optionalNumber(raw.light.range, tile, 'light.range', 0, 64);
    }
    if (raw.editor !== undefined) {
      const look = raw.editor;
      if (!isRecord(look)) fail(tile, 'editor must be an object');
      if (look.color !== undefined && (typeof look.color !== 'string' || !COLOUR.test(look.color)))
        fail(tile, 'editor.color must be #rrggbb');
      if (
        look.label !== undefined &&
        (typeof look.label !== 'string' || !look.label.trim() || look.label.length > 40)
      )
        fail(tile, 'editor.label must be 1–40 characters');
    }
  }
  if (input.stamps !== undefined) {
    if (!Array.isArray(input.stamps) || input.stamps.length > 16)
      fail(what, 'stamps must be a list of at most 16');
    for (const stamp of input.stamps) {
      if (!isRecord(stamp) || typeof stamp.name !== 'string' || !stamp.name.trim())
        fail(what, 'every stamp needs a name');
      const where = `${what} stamp '${stamp.name}'`;
      const cells = stamp.cells;
      if (
        !Array.isArray(cells) ||
        cells.length < 1 ||
        cells.length > 8 ||
        !cells.every(
          (row) =>
            Array.isArray(row) &&
            row.length >= 1 &&
            row.length <= 8 &&
            row.length === (cells[0] as unknown[]).length,
        )
      )
        fail(where, 'cells must be 1–8 rows of 1–8 tiles, all of one length');
      const unknown = (cells as unknown[][])
        .flat()
        .find((t) => typeof t !== 'string' || !seen.has(t));
      if (unknown !== undefined)
        fail(where, `names tile ${JSON.stringify(unknown)}, which the tileset lacks`);
    }
  }
  return input as unknown as TilesetDef;
}

/**
 * Checks an arena against its tileset: the rows decode, every seat's spawn is a tile of its own
 * that a fighter could stand on without burning, a walker can get from each spawn to every other,
 * and the framing makes sense. `tilesets` is looked up by id so that bundled and user-made arenas
 * validate the same way.
 */
export function validateArenaDef(
  input: unknown,
  tilesets: Readonly<Record<string, TilesetDef>>,
): ArenaDef {
  if (!isRecord(input)) fail('arena', 'expected an object');
  const id = input.id;
  if (typeof id !== 'string' || !ID.test(id)) fail('arena', 'id must be kebab-case');
  const what = `arena '${id}'`;
  if (typeof input.name !== 'string') fail(what, 'name is required');

  for (const field of ['width', 'height'] as const) {
    const n = input[field];
    if (!Number.isInteger(n) || (n as number) < 4 || (n as number) > MAX_ARENA_SIZE)
      fail(what, `${field} must be an integer between 4 and ${MAX_ARENA_SIZE}`);
  }
  if (typeof input.tileset !== 'string') fail(what, 'tileset is required');
  const tileset = tilesets[input.tileset];
  if (!tileset) fail(what, `tileset '${input.tileset}' is not one of the known tilesets`);
  if (!isRecord(input.legend) || Object.keys(input.legend).length === 0)
    fail(what, 'legend is required');
  for (const [char, family] of Object.entries(input.legend)) {
    if (char.length !== 1) fail(what, `legend key '${char}' must be a single character`);
    if (typeof family !== 'string') fail(what, `legend '${char}' must name a tile family`);
  }
  if (!Array.isArray(input.rows) || input.rows.length !== input.height)
    fail(what, `rows must hold ${String(input.height)} strings`);

  const camera = input.camera;
  if (input.groups !== undefined) {
    if (!Array.isArray(input.groups) || input.groups.length > 1024) fail(what, 'invalid groups');
    const occupied = new Set<string>();
    for (const group of input.groups) {
      if (
        !isRecord(group) ||
        !['x', 'y', 'width', 'height'].every((k) => Number.isInteger(group[k]))
      )
        fail(what, 'invalid group footprint');
      const { x, y, width, height } = group as {
        x: number;
        y: number;
        width: number;
        height: number;
      };
      if (
        x < 0 ||
        y < 0 ||
        width < 1 ||
        height < 1 ||
        x + width > (input.width as number) ||
        y + height > (input.height as number)
      )
        fail(what, 'group outside arena');
      for (let cy = y; cy < y + height; cy++)
        for (let cx = x; cx < x + width; cx++) {
          const key = `${cx},${cy}`;
          if (occupied.has(key)) fail(what, 'overlapping groups');
          occupied.add(key);
        }
    }
  }
  if (camera !== undefined) {
    if (!isRecord(camera) || (camera.mode !== 'fit' && camera.mode !== 'scroll'))
      fail(what, 'camera.mode must be fit or scroll');
    if (camera.mode === 'scroll') {
      const region = camera.region;
      if (
        !Array.isArray(region) ||
        region.length !== 2 ||
        !region.every((n) => Number.isInteger(n) && (n as number) >= 4)
      )
        fail(what, 'camera.region is required for a scroll arena, as two tile counts');
    }
  }

  optionalNumber(input.timeout, what, 'timeout', 10, 3600);
  optionalNumber(input.suddenDeathAt, what, 'suddenDeathAt', 5, 3600);
  const timeout = (input.timeout as number | undefined) ?? DEFAULT_TIMEOUT;
  const suddenDeathAt = (input.suddenDeathAt as number | undefined) ?? DEFAULT_SUDDEN_DEATH_AT;
  if (suddenDeathAt >= timeout)
    fail(what, `suddenDeathAt (${suddenDeathAt}) must come before timeout (${timeout})`);

  if (input.fog !== undefined) {
    if (!Array.isArray(input.fog)) fail(what, 'fog must be a list of centres');
    for (const centre of input.fog) {
      if (!isRecord(centre) || !Array.isArray(centre.at) || centre.at.length !== 2)
        fail(what, 'every fog centre needs an `at` tile');
      optionalNumber(centre.radius, what, 'fog radius', 0.5, MAX_ARENA_SIZE);
      if (centre.radius === undefined) fail(what, 'every fog centre needs a radius');
    }
  }

  const spawns = input.spawns;
  if (!Array.isArray(spawns) || spawns.length < 2 || spawns.length > MAX_SEATS)
    fail(what, `spawns must be an ordered list of 2 to ${MAX_SEATS} positions`);
  for (const spawn of spawns) {
    if (
      !Array.isArray(spawn) ||
      spawn.length !== 2 ||
      !spawn.every((n) => Number.isInteger(n) && (n as number) >= 0)
    )
      fail(what, 'every spawn must be a [x, y] tile pair');
  }

  // Decoding is the real check on rows and the legend; it throws with the offending cell.
  const arena = input as unknown as ArenaDef;
  const map = new TileMap(arena, tileset);
  const taken = new Set<number>();
  for (const [x, y] of arena.spawns) {
    if (!map.inside(x, y)) fail(what, `spawn ${x},${y} is outside the arena`);
    if (!map.passable(map.tile(x, y), 'ground'))
      fail(what, `spawn ${x},${y} is on '${map.tile(x, y).id}', which nothing can stand on`);
    if ((map.tile(x, y).damagePerSecond ?? 0) > 0)
      fail(what, `spawn ${x},${y} is on '${map.tile(x, y).id}', which burns whoever stands there`);
    if (taken.has(map.index(x, y)))
      fail(what, `two seats spawn on ${x},${y}; every fighter needs a square of its own`);
    taken.add(map.index(x, y));
  }
  // Walled apart, two walkers could never fight: the duel would run to the clock.
  const reached = walkable(map, arena.spawns[0]!);
  for (const [x, y] of arena.spawns)
    if (!reached.has(map.index(x, y)))
      fail(
        what,
        `spawn ${x},${y} cannot be reached on foot from spawn ${arena.spawns[0]![0]},${arena.spawns[0]![1]}`,
      );
  return arena;
}

/** Every tile a walker can reach from a start, stepping square to square (not diagonally). */
function walkable(map: TileMap, [x0, y0]: readonly [number, number]): Set<number> {
  const seen = new Set<number>([map.index(x0, y0)]);
  const queue: [number, number][] = [[x0, y0]];
  for (let i = 0; i < queue.length; i++) {
    const [x, y] = queue[i]!;
    for (const [nx, ny] of [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ] as const) {
      if (!map.inside(nx, ny) || seen.has(map.index(nx, ny))) continue;
      if (!map.passable(map.tile(nx, ny), 'ground')) continue;
      seen.add(map.index(nx, ny));
      queue.push([nx, ny]);
    }
  }
  return seen;
}

/** `timeout` and `suddenDeathAt` with the format's defaults filled in. */
export function arenaTiming(arena: ArenaDef): { timeout: number; suddenDeathAt: number } {
  return {
    timeout: arena.timeout ?? DEFAULT_TIMEOUT,
    suddenDeathAt: arena.suddenDeathAt ?? DEFAULT_SUDDEN_DEATH_AT,
  };
}

/**
 * Seeded random spawn placement (D-19), used when **Use arena starting positions** is off.
 * Every fighter lands on a tile its own movement class can stand on and that does not burn, with
 * every pair as far apart as possible: a fixed number of seeded candidate sets is sampled and the
 * one with the largest minimum pairwise distance wins.
 *
 * Best-effort by design — on a small or awkward map the 20 % target is not always satisfiable
 * with six players, and a duel that cannot start is worse than one that starts close.
 */
export function placeSpawns(
  map: TileMap,
  rng: Rng,
  movements: readonly MovementType[],
  attempts = 24,
): [number, number][] {
  const standable = new Map<MovementType, [number, number][]>();
  for (const movement of new Set(movements)) {
    const cells: [number, number][] = [];
    for (let y = 0; y < map.height; y++)
      for (let x = 0; x < map.width; x++) {
        const tile = map.tile(x, y);
        if (map.passable(tile, movement) && !((tile.damagePerSecond ?? 0) > 0)) cells.push([x, y]);
      }
    if (cells.length === 0)
      throw new Error(`No tile in this arena is standable for a '${movement}' fighter`);
    standable.set(movement, cells);
  }
  const target = 0.2 * Math.hypot(map.width, map.height);
  let best: [number, number][] = [];
  let bestSpread = -1;
  for (let attempt = 0; attempt < attempts; attempt++) {
    const picks: [number, number][] = movements.map((movement) => {
      const cells = standable.get(movement)!;
      return cells[rng.int(cells.length)]!;
    });
    let spread = Infinity;
    for (let i = 0; i < picks.length; i++)
      for (let j = i + 1; j < picks.length; j++)
        spread = Math.min(
          spread,
          Math.hypot(picks[i]![0] - picks[j]![0], picks[i]![1] - picks[j]![1]),
        );
    if (spread > bestSpread) {
      best = picks;
      bestSpread = spread;
    }
    if (bestSpread >= target) break;
  }
  return best;
}
